import { useContext } from 'react';
import { MonthCalendar } from './MonthCalendar';
import { YearSelector } from './YearSelector';
import {
  CalendarContainer,
  CalendarWidgetContainer,
} from './styles/CalendarWidget.styled';
import { getWeekendsOfMonth } from 'src/utils/dateFns/getWeekendsOfMonth';
import { getLastDayOfMonth } from 'src/utils/dateFns/getLastDaysOfMonth';
import { useGetTeamRequests } from 'src/tabs/hooks/useGetTeamRequests';
import { getNonWorkingDaysOfMonth } from 'src/utils/dateFns/findNonWorkingDaysOfMonth';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { VacationsRequest } from '../RequestsWidget/models/RequestWidget.interface';
import { VacationType } from 'src/tabs/hooks/useVacationTypes';
import { ColorCodedEmployee } from '../TeamWidget/TeamWidgetSection';
import { EmployeeVacationDays } from '../../../../tabs/models/SummaryInterfaces';

interface CalendarWidgetProps {
  year: number;
  selectedMonth: number;
  incrementYear: () => void;
  decrementYear: () => void;
  incrementMonth: () => void;
  decrementMonth: () => void;
  holidays: Array<string>;
  team: Array<ColorCodedEmployee>;
  currentUserRequests: VacationsRequest[];
  vacationTypes: VacationType[];
  hideEmployees: boolean;
  vacationPeriodNextYear: EmployeeVacationDays;
  vacationPeriodPrevYear: EmployeeVacationDays;
  loadingVacationPeriod: boolean;
}

export const CalendarWidget = ({
  year,
  decrementYear,
  incrementYear,
  decrementMonth,
  incrementMonth,
  holidays,
  selectedMonth,
  team,
  currentUserRequests,
  vacationTypes,
  hideEmployees,
  vacationPeriodNextYear,
  vacationPeriodPrevYear,
  loadingVacationPeriod,
}: CalendarWidgetProps) => {
  const { isMobile } = useContext(TeamsFxContext);
  const { requests } = useGetTeamRequests(team, year);
  const months = [];
  if (!isMobile) {
    for (let month = 0; month < 12; month++) {
      const weekends = getWeekendsOfMonth(year, month);
      const holidaysDateArray = getNonWorkingDaysOfMonth(year, month, holidays);
      const restrictedDates = [...weekends, ...holidaysDateArray];
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(
        year,
        month,
        getLastDayOfMonth(year, month)
      );
      const monthString = firstDayOfMonth.toLocaleString('default', {
        month: 'long',
      });
      const monthGrid = (
        <MonthCalendar
          hideEmployees={hideEmployees}
          currentUserRequests={currentUserRequests}
          requests={requests}
          year={year}
          key={month}
          monthString={monthString}
          firstDayOfMonth={firstDayOfMonth}
          lastDayOfMonth={lastDayOfMonth}
          month={month}
          weekends={restrictedDates}
          vacationTypes={vacationTypes}
          vacationPeriodNextYear={vacationPeriodNextYear}
          vacationPeriodPrevYear={vacationPeriodPrevYear}
        />
      );
      months.push(monthGrid);
    }
  } else {
    const firstDayOfMonth = new Date(year, selectedMonth, 1);
    const weekends = getWeekendsOfMonth(year, selectedMonth);
    const holidaysDateArray = getNonWorkingDaysOfMonth(
      year,
      selectedMonth,
      holidays
    );
    const restrictedDates = [...weekends, ...holidaysDateArray];
    const lastDayOfMonth = new Date(
      year,
      selectedMonth,
      getLastDayOfMonth(year, selectedMonth)
    );
    const monthString = firstDayOfMonth.toLocaleString('default', {
      month: 'long',
    });
    const mobileCalendar = (
      <MonthCalendar
        hideEmployees={hideEmployees}
        currentUserRequests={currentUserRequests}
        requests={requests}
        year={year}
        key={selectedMonth}
        monthString={monthString}
        firstDayOfMonth={firstDayOfMonth}
        lastDayOfMonth={lastDayOfMonth}
        month={selectedMonth}
        weekends={restrictedDates}
        decrementMonth={decrementMonth}
        incrementMonth={incrementMonth}
        vacationTypes={vacationTypes}
        vacationPeriodNextYear={vacationPeriodNextYear}
        vacationPeriodPrevYear={vacationPeriodPrevYear}
      />
    );
    months.push(mobileCalendar);
  }
  return (
    <CalendarContainer>
      {!isMobile && (
        <YearSelector
          loadingVacationPeriod={loadingVacationPeriod}
          vacationPeriodNextYear={vacationPeriodNextYear}
          vacationPeriodPrevYear={vacationPeriodPrevYear}
          year={year}
          incrementYear={incrementYear}
          decrementYear={decrementYear}
        />
      )}
      <CalendarWidgetContainer>{months}</CalendarWidgetContainer>
    </CalendarContainer>
  );
};
